import React, { useState, useEffect, useCallback, useRef } from "react";
import { Row, Col, Button, Form, Card, Dropdown } from "react-bootstrap";
import { Calendar, User, Filter, X, Check, Search } from "lucide-react";
import { Axios } from "api/axios";

interface DashboardFiltersProps {
  onFiltersChange: (filters: {
    timePeriod: string;
    dateRange: { from: string; to: string };
    createdBy: string;
  }) => void;
}

const DashboardFilters: React.FC<DashboardFiltersProps> = ({
  onFiltersChange,
}) => {
  const [activeTab, setActiveTab] = useState("Today");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const [userOptions, setUserOptions] = useState<any[]>([]);
  const [userLoading, setUserLoading] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const isInitialMount = useRef(true);

  // Filter users based on search term
  const filteredUserOptions = userOptions.filter((user) =>
    user.label.toLowerCase().includes(userSearchTerm.toLowerCase())
  );

  // const dispatch = useDispatch<any>();

  // Fetch user dropdown data directly
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setUserLoading(true);
        const response = await Axios.get("/api/user/All-Users-Dropdown/");
        const users = response?.data || [];
        
        const options = users.map((user: any) => ({
          value: user.user_id,
          label: `${user.first_name} ${user.last_name} (${user.username})`,
      }));
      setUserOptions(options);
      } catch (error) {
        console.error("Failed to fetch users:", error);
        setUserOptions([]);
      } finally {
        setUserLoading(false);
      }
    };

    fetchUsers();
  }, []);


  // Handle time period change
  const handleTimePeriodChange = (period: string) => {
    setActiveTab(period);
    const userIds = selectedUsers.map(user => user.value).join(',');
    updateFilters(period, dateRange, userIds);
  };

  // Handle date range change
  const handleDateRangeChange = (field: "from" | "to", value: string) => {
    const newDateRange = { ...dateRange, [field]: value };
    
    // Validate date range logic
    if (field === "to" && newDateRange.from && newDateRange.to) {
      const fromDate = new Date(newDateRange.from);
      const toDate = new Date(newDateRange.to);
      
      if (toDate < fromDate) {
        // If "to" date is earlier than "from" date, reset "to" date
        newDateRange.to = "";
      }
    }
    
    // If "from" date is changed and it's later than current "to" date, reset "to" date
    if (field === "from" && newDateRange.from && newDateRange.to) {
      const fromDate = new Date(newDateRange.from);
      const toDate = new Date(newDateRange.to);
      
      if (fromDate > toDate) {
        newDateRange.to = "";
      }
    }
    
    setDateRange(newDateRange);
    const userIds = selectedUsers.map(user => user.value).join(',');
    updateFilters(activeTab, newDateRange, userIds);
  };

  // Handle user selection change
  const handleUserChange = (users: any[]) => {
    console.log("Users selected:", users);
    setSelectedUsers(users);
    const userIds = users.map(user => user.value).join(',');
    updateFilters(activeTab, dateRange, userIds);
  };

  // Clear users and search
  const handleClearUsers = () => {
    setSelectedUsers([]);
    setUserSearchTerm("");
    updateFilters(activeTab, dateRange, "");
  };

  // Update filters and notify parent
  const updateFilters = useCallback((
    period: string,
    dates: { from: string; to: string },
    user: string
  ) => {
    console.log("Updating filters:", { period, dates, user });
    onFiltersChange({
      timePeriod: period,
      dateRange: dates,
      createdBy: user,
    });
  }, [onFiltersChange]);

  // Clear all filters
  const clearFilters = () => {
    setActiveTab("Today");
    setSelectedUsers([]);
    // Set today's date range when clearing to default
    setDefaultDateRange("Today");
  };

  // Set default date range based on time period
  const setDefaultDateRange = useCallback((period: string) => {
    const today = new Date();
    let fromDate = "";
    let toDate = today.toISOString().split("T")[0];

    switch (period) {
      case "Today":
        // Set both from and to dates to today
        fromDate = today.toISOString().split("T")[0];
        toDate = today.toISOString().split("T")[0];
        break;
      case "weekly":
        const weekAgo = new Date(today);
        weekAgo.setDate(today.getDate() - 7);
        fromDate = weekAgo.toISOString().split("T")[0];
        break;
      case "monthly":
        const monthAgo = new Date(today);
        monthAgo.setMonth(today.getMonth() - 1);
        fromDate = monthAgo.toISOString().split("T")[0];
        break;
      case "quarterly":
        const quarterAgo = new Date(today);
        quarterAgo.setMonth(today.getMonth() - 3);
        fromDate = quarterAgo.toISOString().split("T")[0];
        break;
    }

    const newDateRange = { from: fromDate, to: toDate };
    setDateRange(newDateRange);
    const userIds = selectedUsers.map(user => user.value).join(',');
    updateFilters(period, newDateRange, userIds);
  }, [selectedUsers, updateFilters]);

  // Set default date range for "Today" on component mount
  useEffect(() => {
    if (isInitialMount.current) {
      const today = new Date();
      const todayStr = today.toISOString().split("T")[0];
      const newDateRange = { from: todayStr, to: todayStr };
      setDateRange(newDateRange);
      updateFilters("Today", newDateRange, "");
      isInitialMount.current = false;
    }
  }, [updateFilters]); // Include updateFilters in dependencies

  return (
    <Card
      className="mb-4 shadow-sm border-0 sticky-filters"
      style={
        {
          borderRadius: "12px",
          "--bs-primary": "#0d6efd",
        } as React.CSSProperties
      }
    >
      <style>
        {`
          .sticky-filters {
            position: sticky;
            top: 0;
            z-index: 1000;
            background: white;
            transition: all 0.3s ease;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
          }
          
          /* Remove sticky positioning on mobile screens */
          @media (max-width: 768px) {
            .sticky-filters {
              position: static;
              top: auto;
              z-index: auto;
            }
          }
          
          .sticky-filters:hover {
            box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
          }
          
          .sticky-filters .card-header {
            position: relative;
            z-index: 1001;
          }
          
          .sticky-filters .card-body {
            position: relative;
            z-index: 1001;
            background: white;
          }
        `}
      </style>
      <Card.Header
        className="text-white border-0 p-2"
        style={{
          borderRadius: "12px 12px 0 0",
          background:
            "linear-gradient(120deg, #005D97 0%,rgb(60, 151, 208) 100%)",
          // "linear-gradient(120deg, rgb(60, 151, 208) 0%,#005D97 100%)",
        }}
      >
        <div className="d-flex w-100 align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <Filter size={20} className="me-2" />
            <span
              style={{
                fontSize: "1rem",
                fontWeight: "bold",
                color: "white",
                textAlign: "center",
              }}
            >
              Dashboard Filters
            </span>
          </div>

          {/* Clear Filters Button */}
          <div>
            <style>
              {`
                .clear-filters-btn {
                  border-radius: 8px;
                  border-width: 2px;
                  transition: all 0.2s ease;
                  max-width: 100px;
                  background-color: #dc3545 !important;
                  border-color: #dc3545 !important;
                  color: #fff !important;
                  }
                  .clear-filters-btn:hover,
                  .clear-filters-btn:focus {
                    background-color: rgb(252, 99, 114) !important;
                    color:rgb(255, 255, 255) !important;
                    border-color:rgb(252, 99, 114) !important;
                }
              `}
            </style>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={clearFilters}
              className="fw-medium clear-filters-btn"
            >
              <X size={16} className="me-2" />
              Clear All
            </Button>
          </div>
        </div>
      </Card.Header>

      <Card.Body className="p-2 pb-3">
        <Row className="gy-4 gx-3 align-items-center">
          {/* Time Period Tabs - 1st col */}
          <Col xl={5} lg={5} md={6} sm={12} xs={12}>
            <div className="filter-group h-100 d-flex flex-column justify-content-between">
              <label className="form-label fw-semibold mb-2 text-dark">
                <Calendar size={16} className="me-2 text-primary" />
                Time Period
              </label>
              <div className="d-flex flex-row flex-sm-wrap gap-2 flex-wrap">
                <style>
                  {`
                    .dashboard-time-period-btn {
                      border-radius: 8px;
                      min-width: 90px;
                      transition: all 0.2s ease;
                      white-space: nowrap;
                    }
                    .dashboard-time-period-btn.border-primary.text-primary:hover,
                    .dashboard-time-period-btn.border-primary.text-primary:focus {
                      background-color: #005D97;
                      color: #fff !important;
                      border-color: #005D97;
                    }
                    .dashboard-time-period-btn:hover,
                    .dashboard-time-period-btn:focus {
                      color: #fff !important;
                    }
                  `}
                </style>
                {[
                  {
                    key: "Today",
                    label: "Today",
                    // Sun icon (today); primary color, turn white if active
                    icon: (
                      <span>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <circle
                            cx="10"
                            cy="10"
                            r="5"
                            fill={activeTab === "Today" ? "#fff" : "#0d6efd"}
                          />
                          <g
                            stroke={activeTab === "Today" ? "#fff" : "#0d6efd"}
                            strokeWidth="1.3"
                          >
                            <line x1="10" y1="1.7" x2="10" y2="4" />
                            <line x1="10" y1="16" x2="10" y2="18.3" />
                            <line x1="16" y1="10" x2="18.3" y2="10" />
                            <line x1="1.7" y1="10" x2="4" y2="10" />
                            <line x1="15.07" y1="4.93" x2="13.64" y2="6.36" />
                            <line x1="6.36" y1="13.64" x2="4.93" y2="15.07" />
                            <line x1="15.07" y1="15.07" x2="13.64" y2="13.64" />
                            <line x1="6.36" y1="6.36" x2="4.93" y2="4.93" />
                          </g>
                        </svg>
                      </span>
                    ),
                  },
                  {
                    key: "weekly",
                    label: "Weekly",
                    // Calendar-week view: grid icon; warning color
                    icon: (
                      <span >
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <rect x="2" y="5" width="16" height="11" rx="2" stroke="#FFC107" strokeWidth="1.3" fill="#FFF8E1"/>
                          <rect x="5" y="9" width="3" height="3" rx="1" fill="#FFC107" />
                          <rect x="9" y="9" width="3" height="3" rx="1" fill="#FFC107" />
                          <rect x="13" y="9" width="3" height="3" rx="1" fill="#FFC107" />
                          <rect x="2" y="3" width="16" height="3" rx="1" fill="#FFC107" opacity="0.3"/>
                        </svg>
                      </span>
                    ),
                  },
                  {
                    key: "monthly",
                    label: "Monthly",
                    // Full calendar-month icon; green success color
                    icon: (
                      <span >
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <rect x="2" y="4" width="16" height="13" rx="2" stroke="#41B883" strokeWidth="1.3" fill="#E6F4EA"/>
                          <rect x="4" y="7" width="3" height="3" rx="1" fill="#41B883" />
                          <rect x="8.5" y="7" width="3" height="3" rx="1" fill="#41B883" />
                          <rect x="13" y="7" width="3" height="3" rx="1" fill="#41B883" />
                          <rect x="4" y="11.5" width="3" height="3" rx="1" fill="#41B883" />
                          <rect x="8.5" y="11.5" width="3" height="3" rx="1" fill="#41B883" />
                          <rect x="13" y="11.5" width="3" height="3" rx="1" fill="#41B883" />
                          <rect x="2" y="2" width="16" height="3" rx="1" fill="#41B883" opacity="0.17"/>
                        </svg>
                      </span>
                    ),
                  },
                  {
                    key: "quarterly",
                    label: "Quarterly",
                    // Pie-chart icon; red color
                    icon: (
                      <span >
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <circle cx="10" cy="10" r="8" stroke="#FA5252" strokeWidth="1.3" fill="#FFE5E7"/>
                          <path d="M10 2v8h8A8 8 0 0010 2z" fill="#FA5252" />
                        </svg>
                      </span>
                    ),
                  }
                ].map((tab) => (
                  <Button
                    key={tab.key}
                    variant={
                      activeTab === tab.key ? "primary" : "outline-primary"
                    }
                    size="sm"
                    onClick={() => {
                      handleTimePeriodChange(tab.key);
                      setDefaultDateRange(tab.key);
                    }}
                    className={`fw-medium px-3 py-2 dashboard-time-period-btn ${
                      activeTab === tab.key
                        ? "shadow-sm"
                        : "border-primary text-primary"
                    }`}
                    style={{
                      boxShadow:
                        activeTab === tab.key
                          ? "0 4px 12px rgba(0, 123, 255, 0.18)"
                          : "none",
                    }}
                  >
                    <span className="me-1">{tab.icon}</span>
                    {tab.label}
                  </Button>
                ))}
              </div>
            </div>
          </Col>

          <Col xl={7} lg={7} md={12} sm={12} xs={12}>
            <Row className="g-3">
              {/* Date Range Filter - Left col (aligns with Created By) */}
              <Col
                xxl={6}
                xl={12}
                lg={12}
                md={12}
                sm={12}
                xs={12}
                className="d-flex align-items-stretch"
              >
                <div className="filter-group w-100 h-100 d-flex flex-column justify-content-between">
                  <label className="form-label fw-semibold mb-2 text-dark">
                    <Calendar size={16} className="me-2 text-primary" />
                    Date Range
                  </label>
                  <div className="d-flex flex-column flex-md-row align-items-stretch gap-2">
                    <Form.Group className="flex-fill mb-0">
                      <Form.Control
                        type="date"
                        placeholder="From Date"
                        value={dateRange.from}
                        onChange={(e) =>
                          handleDateRangeChange("from", e.target.value)
                        }
                        size="sm"
                        className="border-primary"
                        style={{
                          borderRadius: "8px",
                          transition: "all 0.2s ease",
                          minWidth: 0,
                          minHeight: "38px",
                        }}
                      />
                    </Form.Group>
                    <span className="align-self-center fw-medium text-muted">
                      to
                    </span>
                    <Form.Group className="flex-fill mb-0">
                      <Form.Control
                        type="date"
                        placeholder="To Date"
                        value={dateRange.to}
                        onChange={(e) =>
                          handleDateRangeChange("to", e.target.value)
                        }
                        min={dateRange.from || undefined}
                        size="sm"
                        className="border-primary"
                        style={{
                          borderRadius: "8px",
                          transition: "all 0.2s ease",
                          minWidth: 0,
                          minHeight: "38px",
                        }}
                      />
                    </Form.Group>
                  </div>
                </div>
              </Col>

              {/* Created By User Selector - Right col (aligns with Date Range) */}
              <Col
                xxl={6}
                xl={12}
                lg={12}
                md={12}
                sm={12}
                xs={12}
                className="d-flex align-items-stretch"
              >
                <div className="filter-group w-100 h-100 d-flex flex-column justify-content-between">
                  <label className="form-label fw-semibold mb-2 text-dark">
                    <User size={16} className="me-2 text-primary" />
                    Created By
                  </label>
                  <div className="d-flex gap-2">
                    <div className="flex-fill">
                      <Dropdown onToggle={(isOpen) => {
                        if (isOpen) {
                          setUserSearchTerm("");
                        }
                      }}>
                        <Dropdown.Toggle
                          as={Button}
                          variant="outline-primary"
                          className="w-100 d-flex justify-content-between align-items-center"
                          style={{
                            borderRadius: "8px",
                            minHeight: "38px",
                            borderColor: "#005D97",
                            backgroundColor: "#fff",
                            color: "#005D97",
                            borderWidth: "1px",
                            transition: "all 0.2s ease",
                          }}
                        >
                          <span className="text-truncate">
                            {selectedUsers.length === 0
                              ? "Select Users"
                              : selectedUsers.length === 1
                              ? selectedUsers[0].label
                              : `${selectedUsers.length} users selected`}
                          </span>
                        </Dropdown.Toggle>

                        <Dropdown.Menu
                          className="w-100"
                          style={{
                            maxHeight: "300px",
                            overflowY: "auto",
                            borderRadius: "8px",
                            border: "1px solid #e0e0e0",
                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                            padding: "0",
                            marginTop: "4px",
                          }}
                        >
                          {/* Selected Users Section */}
                          {selectedUsers.length > 0 && (
                            <>
                              <Dropdown.Header
                                className="bg-light border-bottom"
                                style={{
                                  fontSize: "0.8rem",
                                  fontWeight: "600",
                                  color: "#005D97",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                <div className="d-flex align-items-center gap-2">
                                  <Check size={14} />
                                  Selected Users ({selectedUsers.length})
                                </div>
                              </Dropdown.Header>
                              {selectedUsers.map((user) => (
                                <Dropdown.Item
                                  key={`selected-${user.value}`}
                                  as="div"
                                  className="d-flex align-items-center gap-2 py-2 px-3"
                                  style={{
                                    backgroundColor: "#e3f2fd",
                                    borderLeft: "3px solid #005D97",
                                  }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleUserChange(
                                      selectedUsers.filter((u) => u.value !== user.value)
                                    );
                                  }}
                                >
                                  <div
                                    className="d-flex align-items-center justify-content-center"
                                    style={{
                                      width: "18px",
                                      height: "18px",
                                      borderRadius: "3px",
                                      backgroundColor: "#005D97",
                                      color: "white",
                                      fontSize: "12px",
                                      flexShrink: 0
                                    }}
                                  >
                                    <Check size={12} />
                                  </div>
                                  <div className="flex-grow-1">
                                    <div 
                                      className="fw-medium text-dark"
                                      style={{
                                        wordWrap: "break-word",
                                        wordBreak: "break-word",
                                        whiteSpace: "normal",
                                        lineHeight: "1.3"
                                      }}
                                    >
                                      {user.label}
                                    </div>
                                  </div>
                                  <X size={14} className="text-muted" />
                                </Dropdown.Item>
                              ))}
                              <Dropdown.Divider />
                            </>
                          )}

                          {/* All Users Section */}
                          <Dropdown.Header
                            className="bg-light border-bottom"
                            style={{
                              fontSize: "0.8rem",
                              fontWeight: "600",
                              color: "#6c757d",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                            }}
                          >
                            All Users
                          </Dropdown.Header>
                          
                          {/* Search Input */}
                          <div className="p-2 border-bottom">
                            <div className="position-relative">
                              <Search 
                                size={16} 
                                className="position-absolute top-50 start-0 translate-middle-y ms-2 text-muted"
                                style={{ zIndex: 1 }}
                              />
                              <Form.Control
                                type="text"
                                placeholder="Search users..."
                                value={userSearchTerm}
                                onChange={(e) => setUserSearchTerm(e.target.value)}
                                style={{
                                  paddingLeft: "2.5rem",
                                  borderRadius: "6px",
                                  border: "1px solid #dee2e6",
                                  fontSize: "0.875rem",
                                }}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                          </div>
                          {userLoading ? (
                            <Dropdown.Item disabled className="text-center py-3">
                              <div className="spinner-border spinner-border-sm me-2" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                              Loading Users...
                            </Dropdown.Item>
                          ) : filteredUserOptions.length === 0 ? (
                            <Dropdown.Item disabled className="text-center py-3 text-muted">
                              {userSearchTerm ? "No users found matching your search" : "No users found"}
                            </Dropdown.Item>
                          ) : (
                            filteredUserOptions.map((user) => {
                              const isSelected = selectedUsers.some(
                                (selected) => selected.value === user.value
                              );
                              return (
                                <Dropdown.Item
                                  key={user.value}
                                  as="div"
                                  className="d-flex align-items-center gap-2 py-2 px-3"
                                  style={{
                                    backgroundColor: isSelected ? "#f8f9fa" : "transparent",
                                  }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (isSelected) {
                                      handleUserChange(
                                        selectedUsers.filter((u) => u.value !== user.value)
                                      );
                                    } else {
                                      handleUserChange([...selectedUsers, user]);
                                    }
                                  }}
                                >
                                  <div
                                    className="d-flex align-items-center justify-content-center"
                                    style={{
                                      width: "18px",
                                      height: "18px",
                                      borderRadius: "3px",
                                      backgroundColor: isSelected ? "#005D97" : "transparent",
                                      border: isSelected ? "none" : "2px solid #dee2e6",
                                      color: isSelected ? "white" : "transparent",
                                      fontSize: "12px",
                                      transition: "all 0.2s ease",
                                      flexShrink: 0
                                    }}
                                  >
                                    {isSelected && <Check size={12} />}
                                  </div>
                                  <div className="flex-grow-1">
                                    <div 
                                      className="fw-medium text-dark"
                                      style={{
                                        wordWrap: "break-word",
                                        wordBreak: "break-word",
                                        whiteSpace: "normal",
                                        lineHeight: "1.3"
                                      }}
                                    >
                                      {user.label}
                                    </div>
                                  </div>
                                </Dropdown.Item>
                              );
                            })
                          )}
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                    {selectedUsers.length > 0 && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleClearUsers}
                        style={{
                          borderRadius: "8px",
                          minWidth: "38px",
                          height: "38px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        title="Clear user selection"
                      >
                        <X size={16} />
                      </Button>
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          </Col>

          {/* Filter Summary (Spans all columns, always on new line) */}
          {/* {(activeTab !== "weekly" ||
            dateRange.from ||
            dateRange.to ||
            selectedUser) && (
            <Col xs={12}>
              <div className="mt-2 mt-md-4 p-3 bg-light rounded-3 border-start border-primary border-4">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <div className="d-flex align-items-center flex-wrap gap-2">
                    <Filter size={16} className="me-2 text-primary" />
                    <small className="text-dark fw-medium">
                      <strong>Active Filters:</strong>
                    </small>
                  </div>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={clearFilters}
                    className="text-danger p-0"
                    style={{ textDecoration: "none" }}
                  >
                    <X size={14} />
                  </Button>
                </div>
                <div className="mt-2">
                  <div className="d-flex flex-wrap gap-2">
                    {activeTab !== "weekly" && (
                      <span className="badge bg-primary text-white px-3 py-2 rounded-pill">
                        Time:{" "}
                        {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
                      </span>
                    )}
                    {dateRange.from && dateRange.to && (
                      <span className="badge bg-info text-white px-3 py-2 rounded-pill">
                        Date: {dateRange.from} to {dateRange.to}
                      </span>
                    )}
                    {selectedUser && (
                      <span className="badge bg-success text-white px-3 py-2 rounded-pill">
                        User:{" "}
                        {userOptions.find((u) => u.value === selectedUser)
                          ?.label || selectedUser}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </Col>
          )} */}
        </Row>
      </Card.Body>
    </Card>
  );
};

export default DashboardFilters;
